<script setup>
import { getCurrentInstance, inject, onBeforeMount, ref } from 'vue';
import {useRoute, useRouter} from "vue-router/composables";

const context = inject("applicationContext");
const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const store = proxy.$store;

const props = defineProps({
	application: Object,
	allowedSteps: Array,
	component: Object,
	type: String
});

const doesApplicationHaveApplicant = () => props?.application?.application?.firstName && props?.application?.application?.lastName;

const stepConfigs = {//TODO: move to config file
	applicants: {
		disableAllNextSteps: !doesApplicationHaveApplicant(),
		label: 'Applicants',
		order: 2,
		requiresValidationOnNext: true,
		route: `${props.type ?? ""}Applicants`,
	},
	bank: {
		hideButtons: true,
		label: "Bank Verification",
		order: 2,
		route: "BankVerification",
		skipSave: true
	},
	calculator: {
		disableAfter: route.params.applicationId === "new" ? 0 : !doesApplicationHaveApplicant() ? 1 : undefined,
		disableAllNextSteps: !doesApplicationHaveApplicant(),
		disableSteps: route.params.applicationId === "new",
		label: "Calculator",
		order: 1,
		requiresConfirmation: true,
		requiresValidationOnNext: true,
		route: `${props.type ?? ""}Calculator`,
		skipSave: true
	},
	completion: {
		label: "Completion",
		order: 3,
		route: "CompletionCertificate",
		skipSave: true
	},
	documents: {
		label: "Uploads",
		order: 4,
		route: "UploadFiles",
		skipSave: true
	},
	equipment: {
		label: "Equipment",
		order: 3,
		route: `${props.type ?? ""}Equipment`,
		skipSave: true
	},
	sign: {
		label: "Sign",
		order: 1,
		route: "Sign",
		skipSave: true
	},
	save: {
		label: "Submit",
		order: 4,
		nextButtonText: "forms.submit",
		requiresValidationOnNext: true,
		route: `${props.type ?? ""}Save`,
		skipSave: true
	},
	submit: {
		label: "Review & Submit",
		order: 5,
		nextButtonText: "forms.submit",
		requiresValidationOnNext: true,
		route: "ApplicationDashboard",
		skipSave: true
	},
};

const current = ref(0); //Current step index
const exiting = ref(false); //Flag for letting children and parent know
const nextButtonText = ref("forms.next");
const nextButtonPopoverText = ref("");
const nextDisabled = ref(true);
const nextStep = ref(false);
const previousStep = ref(false);
const requiresConfirmation = ref(false);
const requiresValidationOnNext = ref(false);
const saveStep = ref({
	onError: () => {
		if (nextStep.value) {
			nextStep.value = false;
		} else if (previousStep.value) {
			previousStep.value = false;
		} else if (exiting.value) {
			exiting.value = false;
		}
		saveStep.value.saving = false;
	},
	saving: false
});
const showConfirmation = ref(false);
const skipSave = ref(false);
const steps = ref([]);

const convertSteps = () => {
	const newSteps = [];
	for(let step of props.allowedSteps) {
		const foundStep = stepConfigs?.[step];
		if (foundStep) {
			newSteps.push({
				...foundStep,
				name: step
			});
		}
	}
	steps.value = newSteps.sort((a, b) => a.order - b.order);
	current.value = steps.value.findIndex(step => step.route === route.name);
	nextButtonText.value = steps.value?.[current.value]?.nextButtonText ?? "forms.next";
	requiresConfirmation.value = steps.value?.[current.value]?.requiresConfirmation ?? false;
	requiresValidationOnNext.value = steps.value?.[current.value]?.requiresValidationOnNext ?? false;
	skipSave.value = steps.value?.[current.value]?.skipSave ?? false;
}

const exitApplication = () => {
	resetStates();
	context.clearApplication();
	router.push({ name: "Dashboard" });
}

const getApplicationDetails = async () => {
	const id = route.params.applicationId;
	if (id !== "new") {
		return await context.getApplication(id);
	}
}

const getRoute = (step, params, query) => {
	console.log(step)
	return {
		name: step.route,
		params: params ?? route.params,
		query: query ?? {},
	}
}

const findNextStep = (name) => {
	let result;
	let foundStep = steps.value.findIndex(step => step.name === name);
	if (foundStep !== -1) {
		result = foundStep
	} else {
		store.dispatch('showErrorAlert', "Step not found");
	}
	return result;
}

const goToStep = ({ index, name, params, query, routeName }) => {
	if (skipSave.value || saveStep.value?.saving) {
		resetStates();
		if (name) {
			index = findNextStep(name);
		}
		if (current.value !== index) {
			if (routeName) { //Normally for if were routing outside of regular process
				router.push({ name: routeName, params: params, query: query });
			} else {
				current.value = index;
				router.push(getRoute(steps.value[index], params, query));
			}
		}
	} else if (!saveStep.value.saving) {
		saveStep.value.onSuccess = () => goToStep({
			index: index,
			name: name,
			params: params,
			query: query,
			routeName: routeName
		});
		saveStep.value.saving = true;

	}
}

const goToNextStep = (params, query) => {
	let newValue = current.value;
	if (newValue < (steps.value.length - 1)) {
		newValue++;
	}
	goToStep({ index: newValue, params: params, query: query });
}

const goToPreviousStep = (params, query) => {
	let newValue = current.value;
	if (newValue > 0) {
		newValue--;
	}
	goToStep({ index: newValue, params: params, query: query });
}

const isDisabled = (index) => {
	let result = saveStep.value?.saving ?? false;
	const stepConfig = steps.value?.[current.value] ?? {};
	if ((stepConfig.disableAfter ?? current.value) < index) {
		if ((nextDisabled.value && stepConfig.disableAllNextSteps) || stepConfig.disableAfter || stepConfig.disableSteps) {
			result = true;
		}
	}
	return result;
}

const isHidden = () => {
	const stepConfig = steps.value?.[current.value] ?? {};
	return stepConfig.hideButtons ?? false;
}

const isCurrent = (index) => {
	return (index === current.value) ? 'current' : '';
}

const resetStates = () => {
	exiting.value = false;
	nextButtonText.value = "forms.next";
	nextButtonPopoverText.value = "";
	nextDisabled.value = true;
	nextStep.value = false;
	previousStep.value = false;
	requiresConfirmation.value = false;
	requiresValidationOnNext.value = false;
	saveStep.value = {
		onError: () => {
			if (nextStep.value) {
				nextStep.value = false;
			} else if (previousStep.value) {
				previousStep.value = false;
			} else if (exiting.value) {
				exiting.value = false;
			}
			saveStep.value.saving = false;
		},
		onSuccess: () => {},
		saving: false
	};
	showConfirmation.value = false;
	skipSave.value = false;
}

const setNextDisabled = (newValue = false) => {
	nextDisabled.value = newValue;
}

const setNextPopoverText = (newValue = "") => {
	nextButtonPopoverText.value = newValue;
}

const nextStepConditions = () => {
	if (!requiresConfirmation.value || showConfirmation.value) {
		if (requiresValidationOnNext.value) {//Requiring validation overrides skipping straight to moving to the next step
			nextStep.value = !nextDisabled.value;
		} else if (skipSave.value) {//If the page requires a save first
			goToNextStep();
		} else {
			nextStep.value = true;
		}
	} else {
		showConfirmation.value = true;
	}
}

const previousStepConditions = () => {
	if (showConfirmation.value) {
		showConfirmation.value = false;
	} else {
		if (skipSave.value) {
			goToPreviousStep();
		} else {
			previousStep.value = true;
		}
	}
}

const saveForLaterConditions = () => {
	if (skipSave.value) {
		exitApplication();
	} else {
		exiting.value = true;
	}
}

onBeforeMount(() => {
	resetStates();
	convertSteps();
});

const componentProps = () => ({
	application: props.application, //Determine whether the application needs to be retrieved
	exitApplication: exitApplication, //Callback for existing the application to Dashboard
	exiting: exiting.value, //Exiting flag to allow child component to save
	getApplication: getApplicationDetails,
	goToNextStep: goToNextStep, //Callback for going to the next step in the process
	goToPreviousStep: goToPreviousStep, //Callback for going to the next step in the process
	goToStep: goToStep, //Manually decide next step
	is: props.component, //Component passed down from the parent component to render in between steps
	nextStep: nextStep.value, //Next step flag to allow child component to save
	previousStep: previousStep.value, //Previous step flag to allow child component to save
	saveStep: saveStep.value, // Object to allow callback functions and flags to deal with business rules on navigation
	setNextDisabled: setNextDisabled, //Callback to allow the component to let steps now when you're allowed to click next
	setNextPopoverText: setNextPopoverText, //Title for showing errors
	showConfirmation: showConfirmation.value, //Some components require a confirmation first before moving to the next step, this will allow this
	type: props.type //Application Type
});

</script>

<template>
	<div>
		<div class="stepper theming tertiary-color">
			<div class="container-lg px-0">
				<div class="row">
					<div class="col-md-24">
						<div class="d-flex flex-row stepper-m">
							<div class="stepper__steps flex-grow-1" v-for="(step, index) in steps" :key="index">
								<div class="stepper__steps__step">
									<span
										aria-disabled="true"
										class="stepper__steps__step__icon stepper__steps__step__icon--disabled"
										:class="[isCurrent(index)]"
										title="Save the application before opening this step"
										v-if="isDisabled(index)">{{ index + 1 }}
									</span>
									<span
										class="stepper__steps__step__icon"
										:class="[isCurrent(index)]"
										@click="goToStep({ index: index })"
										v-else >
										{{ index + 1 }}
									</span>
									<div class="stepper__steps__step__label">
										<p class="fw-bold">{{ step.label ?? "Label" }}</p>
									</div>
								</div>
								<div v-if="index < (steps.length - 1)" class="stepper__steps__line current">
									<hr>
								</div>
								<div v-else class="stepper__steps__line" style="visibility: hidden;">
									<hr>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<component v-bind="componentProps()" />
		<div class="tffi-navigation-bottom">
			<div class="container-xl">
				<div class="row">
					<div class="col order-3 order-lg-1 col-24 col-lg-16 d-flex justify-content-start" v-if="current > 0 || showConfirmation">
						<button class="tffi-btn tffi-btn--solid theming secondary-color" @click="previousStepConditions" color="primary" :hidden="isHidden()" type="button" >
							{{ $t('forms.back') }}
						</button>
					</div>
					<div class="col d-flex justify-content-start" v-if="route.params.applicationId === 'new' && !showConfirmation">
						<button class="tffi-btn tffi-btn--solid theming secondary-color" @click="exitApplication" :hidden="isHidden()" type="button">
							Cancel
						</button>
					</div>
					<div class="col order-3 d-flex justify-content-end msl-5">
						<div class="order-2 d-flex justify-content-end msl-5" v-if="route.params.applicationId !== 'new'">
							<button class="tffi-btn tffi-btn--solid theming secondary-color ms-auto" @click="saveForLaterConditions" :hidden="isHidden()" type="button" >
								Save for later
							</button>
						</div>
						<div class="order-2 d-flex justify-content-end ms-5">
							<button class="tffi-btn  tffi-btn--solid theming primary-color me-0" @click="nextStepConditions" :disabled="requiresValidationOnNext && nextDisabled" :hidden="isHidden()" :title="nextButtonPopoverText ?? ''" >
								{{ $t(nextButtonText) }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
/* All sizes: icons level & labels centered */
.stepper-m {
	justify-content: space-evenly;
}

.stepper__steps__step {
	justify-content: flex-start !important;
}

.stepper__steps {
	&:first-child .stepper__steps__step {
		margin-right: 0 !important;
	}
	&:last-child .stepper__steps__step {
		margin-left: 0 !important;
	}
}

.stepper__steps__step__label p {
	text-align: center;
}

.stepper__steps__step__icon--disabled {
	cursor: not-allowed;
	opacity: 0.45;
	pointer-events: none;
}

/* Mobile: compact stepper */
@media (max-width: 991px) {
	.stepper-m {
		margin-left: 0 !important;
	}

	.stepper__steps__line {
		display: none !important;
	}

	.stepper {
		min-height: auto !important;
		padding-top: 16px !important;
		padding-bottom: 12px !important;
	}

	.stepper__steps__step__icon {
		height: 28px !important;
		width: 28px !important;
		font-size: 0.75rem !important;
		margin-bottom: 6px !important;
	}

	.stepper__steps__step__label p {
		font-size: 0.625rem !important;
		margin-bottom: 0;
	}
}

/* Desktop: keep original line visibility */
@media (min-width: 992px) {
	.stepper__steps__line {
		display: flex;
	}
}

.steps-form .steps-row {
	width: 100%;
	display: flex;
	justify-content: space-between;
	color: $blue;

	&:before {
		top: 20px;
		left: 10px;
		right: 10px;
		width: auto;
		height: 2px;
		background-color: $blue;
	}

	.steps-step {
		display: block !important;

		@media all and (-ms-high-contrast:none) {
			margin-left: auto;
			margin-right: auto;
		}

		.btn-circle {
			opacity: 1 !important;
			font-size: 1rem;
			font-weight: bold;
			padding: 8px 0;
			width: 2.5rem;
			height: 2.5rem;
			border-radius: 2rem;
			box-shadow: none;
			margin-right: 0;
			margin-left: 0;

			&.btn-info {
				padding: 9px 0;

				&:before {
					position: absolute;
					top: 20px;
					left: 10px;
					right: 10px;
					width: auto;
					height: 2px;
					background-color: $blue;
				}
			}

			&.btn-white.btn-outline-primary {

				&,
				&:hover,
				&:focus,
				&:active {
					background: $white !important;
				}
			}
		}

	}

}
</style>
